'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download } from '@mui/icons-material';
import { Box, Button, IconButton, ListItemIcon, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import * as Yup from 'yup';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useQueryAtmEvents, useUpdateAtmEvent } from '@/hooks/api/useAtms';
import { useNotification } from '@/contexts/NotificationContext';
import { formatDate } from '@/stack-shared/utils/gen-utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const AtmEventPage: React.FC = () => {
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const [startDate, setStartDate] = useState<Date | null>(lastMonth);
  const [endDate, setEndDate] = useState<Date | null>(now);

  const queryAtmEventsMutation = useQueryAtmEvents();
  const updateAtmEventMutation = useUpdateAtmEvent();
  const { showSuccess, showError } = useNotification();

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'date', header: 'Date',
            Cell: ({ row }: any) => {
              return formatDate(row?.original?.date);
           },
     },
    { accessorKey: 'time', header: 'Time' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'assetId', header: 'Asset ID' },
    { accessorKey: 'status', header: 'Status' },
    { accessorKey: 'event', header: 'Event' },
    { accessorKey: 'load', header: 'Load' },
    { accessorKey: 'sourceOfFill', header: 'Source of Fill' },
    { accessorKey: 'balanceAtDeparture', header: 'Balance at Departure' },
    { accessorKey: 'resolveTime', header: 'Resolve Time' },
    { accessorKey: 'resolveDate', header: 'Resolve Date',
      Cell: ({ row }: any) => {
        return formatDate(row?.original?.resolveDate);
     },
     },
     { accessorKey: 'initials', header: 'Technician', enableSorting: false},
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        const rowData = row.original;
        const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
        const actionsOpen = Boolean(actionsAnchorEl);

        const handleMenuOpen = (event: any) => {
          setActionsAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
          setActionsAnchorEl(null);
        };

        return (
          <>
            <IconButton onClick={handleMenuOpen} color="primary" title="Options">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => {
                setSelectedEvent(rowData);
                setEditModalVisible(true);
              }}>
                <ListItemIcon><EditIcon /></ListItemIcon>
                <Typography>Edit</Typography>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'ATM Events' },
  ];

  const rightContent = (
    <>
      <Button variant="outlined" startIcon={<Download />} sx={{ textTransform: 'none' }}>
        Export
      </Button>
      {/* <Button onClick={() => setCreateModalVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        + Add Event
      </Button> */}
    </>
  );

  const sharedEventFields = [{
    groupName: 'main',
    fields: [
      { label: 'Date', name: 'date', type: GENERIC_FORM_FIELD_TYPE.DATE, required: true, columns: 6 },
      { label: 'Time', name: 'time', type: GENERIC_FORM_FIELD_TYPE.TIME, required: false, columns: 6 },
      { label: 'Location', name: 'location', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Asseet ID', name: 'assetId', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Status', name: 'status', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Event', name: 'event', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Technician', name: 'technician', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
    ],
  }];

  const editEventFields = cloneDeep(sharedEventFields);

  const createEventFields = cloneDeep(sharedEventFields);
  createEventFields[0].fields.push(
    { label: 'Load', name: 'load', type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
    { label: 'Source of Fill', name: 'sourceOfFill', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
    { label: 'Balance at Departure', name: 'balanceAtDeparture', type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 12 }
  );

  const fetchData = async () => {
    if (!startDate || !endDate) return;

    queryAtmEventsMutation.mutate({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      skip: pagination.pageIndex * pagination.pageSize,
      take: pagination.pageSize,
      search,
      orderBy: sorting[0]?.id || 'id',
      orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
    }, {
      onSuccess: (result: any) => {
        setData(result.data || []);
        setTotalRows(result.total || 0);
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to fetch ATM events');
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, search, tableRefreshKey, startDate, endDate]);

  return (
    <>
      <GenericFormModal 
        visible={createModalVisible} 
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          console.log("values?", values);
          // Dispatch create action
          setCreateModalVisible(false);
          setTableRefreshKey(tableRefreshKey + 1);
        }}
        initialValues={{}}
        fieldGroups={createEventFields}
        title={'Create ATM Event'} 
      />

      <GenericFormModal 
        visible={editModalVisible} 
        onDismiss={() => setEditModalVisible(false)}
        onSubmit={async (values: any) => {
          updateAtmEventMutation.mutate(values, {
            onSuccess: (data: any) => {
              console.log("Updated", data);
              setEditModalVisible(false);
              setTableRefreshKey(tableRefreshKey + 1);
              showSuccess("ATM event updated successfully");
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update ATM event');
            },
          });
        }}
        initialValues={{
          id: selectedEvent?.id,
          date: selectedEvent?.date,
          time: selectedEvent?.time,
          location: selectedEvent?.location,
          assetId: selectedEvent?.assetId,
          status: selectedEvent?.status,
          event: selectedEvent?.event,
          technician: selectedEvent?.technician,
        }}
        fieldGroups={editEventFields}
        title={'Edit ATM Event'} 
      />

      <TitleBar breadcrumbs={breadcrumbs} title="ATM Events" rightContent={rightContent} />

      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
        renderTopToolbarCustomActions={() => (
          <Box display="flex" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" gap={2}>
              {/* Start Date Picker */}
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                    setStartDate(newValue.toDate());
                  } else if (newValue instanceof Date) {
                    setStartDate(newValue);
                  } else {
                    setStartDate(null);
                  }
                }}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
              />

              {/* End Date Picker */}
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue === 'object' && 'toDate' in newValue) {
                    setEndDate(newValue.toDate());
                  } else if (newValue instanceof Date) {
                    setEndDate(newValue);
                  } else {
                    setEndDate(null);
                  }
                }}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
              />
            </Box>
          </LocalizationProvider>

          <TextField placeholder="Search" variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: '300px' }} />

        </Box>


        )}
      />
    </>
  );
};

export default AtmEventPage;
